/** @format */

import { useState, useEffect, useRef, useCallback, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { GetWidget } from '../widget';
import useGridConfigs, { ROW_MARGIN } from 'src/hooks/useGridConfigs';
// api
import { useDispatch, useSelector } from 'src/store';
import { getWidgetInfo } from 'src/slices/users';
import useAuth from 'src/hooks/useAuth';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  buildResponsiveLayouts,
  generateLayout,
  getBreakpointFromWidth,
  normalizeLayoutItem,
  normalizeLayouts
} from 'src/utils/widget';
import type { WidgetDashboardResponse } from 'src/types';

export { generateLayout };

const ResponsiveReactGridLayout = WidthProvider(Responsive);

/** Grid items are positioned with CSS transforms, so focusing a link scrolls to its layout box. */
const preventTransformedLinkScroll = (event: ReactMouseEvent<HTMLElement>) => {
  if (event.button !== 0) return;
  const target = event.target;
  if (!(target instanceof Element) || !target.closest('a[href]')) return;
  event.preventDefault();
};

export { ROW_MARGIN, ROW_HEIGHT_MOBILE, ROW_HEIGHT_DEFAULT, getGridConfigs } from 'src/hooks/useGridConfigs';

const debouncedLayoutClassUpdate = _.debounce(callback => {
  callback();
}, 100);

const debouncedSizeUpdate = _.debounce(callback => {
  callback();
}, 100);

export const WidgetGrid = ({ dashboardInfo }: { dashboardInfo?: WidgetDashboardResponse | null } = {}) => {
  const gridConfigs = useGridConfigs();
  const rowHeight = gridConfigs.rowHeight;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { widgetInfo: reduxWidgetInfo } = useSelector(state => state.users);
  const isSubDashboard = dashboardInfo !== undefined;
  const widgetInfo = isSubDashboard ? dashboardInfo : reduxWidgetInfo;
  const [globalLayouts, setLayouts] = useState<Record<string, Array<Record<string, unknown>>>>({});
  const [widgets, setWidgets] = useState([]);
  const [currentBreakPoint, setCurrentBreakPoint] = useState(null);
  const gridLayoutRef = useRef(null);
  const currentSize = useRef([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [visible, setVisible] = useState(false);

  // avoid flash of unstyled content
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isSubDashboard || !user || widgetInfo) return;
    dispatch(getWidgetInfo());
  }, [user, widgetInfo, isSubDashboard, dispatch]);

  useEffect(() => {
    if (!user || !widgetInfo) return;

    const widgetData = widgetInfo?.data || [];

    if (isSubDashboard) {
      setWidgets(widgetData);
      setLayouts({});
      return;
    }

    if (widgets.length === 0) {
      setWidgets(widgetData);
    }
  }, [user, widgetInfo, isSubDashboard]);

  useEffect(() => {
    if (!widgets.length) return;
    if (globalLayouts.md?.length === widgets.length) return;

    const allLayouts = buildResponsiveLayouts(widgets);
    const activeBreakpoint = getBreakpointFromWidth();

    const newWidgets = widgets.map((item, index) => {
      const dataGrid = allLayouts[activeBreakpoint][index];
      return {
        ...item,
        i: dataGrid.i,
        x: dataGrid.x,
        y: dataGrid.y,
        w: dataGrid.w,
        minW: dataGrid.minW,
        maxW: dataGrid.maxW,
        h: dataGrid.h,
        minH: dataGrid.minH,
        maxH: dataGrid.maxH
      };
    });

    setWidgets(newWidgets);
    setLayouts(allLayouts);
  }, [widgets.length]);

  useEffect(() => {
    if (lastUpdate && lastUpdate.widgets && lastUpdate.layouts) {
      setWidgets(lastUpdate.widgets);
      setLayouts(
        normalizeLayouts({
          ...lastUpdate.layouts.layouts,
          [lastUpdate.layouts.breakpoint]: lastUpdate.layouts.newLayout
        })
      );
    }
  }, [lastUpdate]);

  const onLayoutChange = useCallback((currentLayout, allLayouts) => {
    const normalized = normalizeLayouts(allLayouts);

    requestAnimationFrame(() => {
      setLayouts(prev => (_.isEqual(prev, normalized) ? prev : normalized));
    });

    debouncedLayoutClassUpdate(() => {
      if (gridLayoutRef.current && window.innerWidth < 1024) {
        gridLayoutRef.current.classList.add('dashboard-widgets-mobile');
      }
    });
  }, []);

  const onBreakpointChange = useCallback(newBreakpoint => {
    setCurrentBreakPoint(newBreakpoint);
  }, []);

  const normalizedLayouts = useMemo(() => normalizeLayouts(globalLayouts), [globalLayouts]);

  const layoutConfig = useMemo(
    () => ({
      layouts: normalizedLayouts,
      onLayoutChange,
      onBreakpointChange,
      autoSize: true,
      ...gridConfigs
    }),
    [normalizedLayouts, onLayoutChange, onBreakpointChange, gridConfigs]
  );

  const handleItemSizeChange = useCallback(
    (height, i) => {
      if (Object.keys(globalLayouts).length === 0 || !currentBreakPoint) return;

      const newH = Math.ceil(height / (rowHeight + ROW_MARGIN / 2)) + 0.5;
      if (currentSize.current[i] === newH) return;

      debouncedSizeUpdate(() => {
        const currentWidgets = lastUpdate?.widgets || widgets;
        const currentLayouts = { ...globalLayouts };
        const layoutItem = currentLayouts[currentBreakPoint][i];
        const normalizedItem = normalizeLayoutItem({ ...layoutItem, h: newH });

        const newLayoutBreakpoint = currentLayouts[currentBreakPoint].map((item, index) =>
          index === i ? normalizedItem : item
        );

        const newWidgets = currentWidgets.map((item, index) => {
          if (index === i) {
            return { ...item, h: normalizedItem.h, minH: normalizedItem.minH, maxH: normalizedItem.maxH };
          }
          const existingLayout = currentLayouts[currentBreakPoint][index];
          return { ...item, h: existingLayout.h };
        });

        setLastUpdate({
          layouts: {
            layouts: currentLayouts,
            breakpoint: currentBreakPoint,
            newLayout: newLayoutBreakpoint
          },
          widgets: newWidgets
        });

        currentSize.current[i] = normalizedItem.h;
      });
    },
    [globalLayouts, currentBreakPoint, rowHeight, widgets, lastUpdate]
  );

  const layoutsReady = globalLayouts.md?.length === widgets.length;

  return (
    <div
      className="container"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.25s ease-out'
      }}>
      <div className="dashboard-widgets-outer">
        {widgets.length > 0 && layoutsReady && (
          <div className={`dashboard-widgets`} ref={gridLayoutRef} onMouseDown={preventTransformedLinkScroll}>
            <ResponsiveReactGridLayout {...layoutConfig}>
              {widgets.map((item, index) => {
                return (
                  <div key={index} data-size={item.size}>
                    <GetWidget
                      globalLayouts={normalizedLayouts}
                      currentBreakPoint={currentBreakPoint}
                      data={item}
                      onSizeChange={height => handleItemSizeChange(height, index)}
                    />
                  </div>
                );
              })}
            </ResponsiveReactGridLayout>
          </div>
        )}
      </div>
    </div>
  );
};
